import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { SharedModule } from '@app/shared/shared.module';
import { AutocompleteItemModel } from './autocomplete-data.model';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

@Component({
    selector: 'c-autocomplete',
    imports: [SharedModule],
    templateUrl: './autocomplete.component.html',
    styleUrls: ['./autocomplete.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AutoCompleteComponent implements OnDestroy {
  @Input() label: string;
  @Input() control: FormControl;
  @Input() list: AutocompleteItemModel[];
  @Input() icon: IconProp;
  @Input() iconStart = false;
  @Input() required: boolean = false;
  @Input() clearInput: boolean = true;

  @Output() itemSelected = new EventEmitter<AutocompleteItemModel>();

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  private readonly listResize = new ResizeObserver(([entry]) => this.revealList(entry.target as HTMLElement));

  @ViewChild('listRef', { read: ElementRef }) set listRef(ref: ElementRef<HTMLElement> | undefined) {
    this.listResize.disconnect();

    if (!ref) {
      this.host.nativeElement.style.paddingBottom = '';
      return;
    }

    this.listResize.observe(ref.nativeElement);
  }

  ngOnDestroy(): void {
    this.listResize.disconnect();
  }

  select(value: AutocompleteItemModel): void {
    this.itemSelected.next(value);
  }

  /**
   * Reserves the dropdown's height below the input, then scrolls it into view.
   *
   * The reservation is what makes the scroll possible at all: the list is `position: absolute`, and an
   * overflowing absolute box does not grow the scroll container's scrollable area past its own bottom, so
   * without in-flow space behind it the scroller is already at its end and cannot lift the list clear of a
   * page footer. The padding is invisible — the dropdown covers exactly that strip.
   *
   * `block: 'nearest'` then scrolls the smallest amount needed, honouring the `scroll-margin-bottom` that
   * keeps the list off a footer, and reaches the real scroller even though that is `ion-content`'s
   * shadow-DOM `.inner-scroll`. It waits a frame because the padding written just above has not been laid
   * out yet, and scrolling against the stale scrollable area is a no-op.
   */
  private revealList(list: HTMLElement): void {
    this.host.nativeElement.style.paddingBottom = `${list.offsetHeight}px`;
    requestAnimationFrame(() => list.scrollIntoView({ block: 'nearest' }));
  }
}
